import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ChevronLeft,
  Filter,
  Hash,
  MapPin,
  Search,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface SelectableItem {
  id: number;
  name: string;
  code?: string;
  area?: string;
  regionId?: number;
}

interface Area {
  ID: number;
  Name: string;
  totalClients: number;
  depart?: string;
}

interface SelectedEmployee {
  _id: string;
  empName: string;
  avatar?: string;
}

interface TwoColumnSelectListProps {
  availableItems: SelectableItem[];
  selectedItems: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  title: string;
  searchPlaceholder: string;
  emptyMessage?: string;
  className?: string;
  selectedEmployee?: SelectedEmployee;
  avatarServerPath?: string;
  areas?: Area[];
}

interface VirtualizedListProps {
  items: SelectableItem[];
  onItemClick: (itemId: number) => void;
  actionIcon: 'add' | 'remove';
  emptyMessage: string;
  searchTerm: string;
  focusedIndex: number;
  onFocusedIndexChange: (index: number) => void;
  isSearchFocused: boolean;
  areas?: Area[];
}

function VirtualizedList({
  items,
  onItemClick,
  actionIcon,
  emptyMessage,
  searchTerm,
  focusedIndex,
  onFocusedIndexChange: _onFocusedIndexChange,
  isSearchFocused,
  areas,
}: VirtualizedListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 75, // Account for item height + margin + padding
    overscan: 10,
  });

  // Auto-scroll to focused item
  useEffect(() => {
    if (isSearchFocused && focusedIndex >= 0 && focusedIndex < items.length) {
      virtualizer.scrollToIndex(focusedIndex, { align: 'center' });
    }
  }, [focusedIndex, isSearchFocused, virtualizer, items.length]);

  // Highlight search term
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          className="rounded bg-yellow-200 px-0.5 text-yellow-900 dark:bg-yellow-700 dark:text-yellow-100"
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  if (items.length === 0) {
    return (
      <div className="flex h-[600px] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        <div className="text-center">
          <Sparkles className="mx-auto mb-2 h-8 w-8 opacity-50" />
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className="h-[600px] overflow-auto p-3 focus:outline-none"
      style={{
        contain: 'strict',
      }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = items[virtualItem.index];
          const isFocused =
            isSearchFocused && focusedIndex === virtualItem.index;

          return (
            <div
              key={virtualItem.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <div className="mb-1 p-2">
                <div
                  className={cn(
                    'group relative cursor-pointer rounded-xl border transition-colors duration-300',
                    'shadow-sm backdrop-blur-sm hover:shadow-lg',
                    isFocused && [
                      'z-10 ring-2 ring-offset-2',
                      actionIcon === 'add'
                        ? 'border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 ring-blue-400 dark:border-blue-600 dark:from-blue-900/50 dark:to-indigo-900/50 dark:ring-blue-500'
                        : 'border-red-300 bg-gradient-to-r from-red-50 to-pink-50 ring-red-400 dark:border-red-600 dark:from-red-900/50 dark:to-pink-900/50 dark:ring-red-500',
                    ],
                    !isFocused && [
                      'border-slate-200/50 bg-white/80 hover:border-slate-300 dark:border-slate-700/50 dark:bg-slate-800/80',
                      actionIcon === 'add'
                        ? 'hover:border-blue-300 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-indigo-50/80 dark:hover:border-blue-600 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30'
                        : 'hover:border-red-300 hover:bg-gradient-to-r hover:from-red-50/80 hover:to-pink-50/80 dark:hover:border-red-600 dark:hover:from-red-900/30 dark:hover:to-pink-900/30',
                    ],
                    'p-3',
                  )}
                  onClick={() => onItemClick(item.id)}
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                  <div className="relative flex items-center space-x-4 rtl:space-x-reverse">
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          'truncate text-sm font-semibold transition-colors',
                          isFocused
                            ? 'text-slate-900 dark:text-white'
                            : 'text-slate-800 dark:text-slate-100',
                        )}
                      >
                        <User className="mr-2 inline h-3.5 w-3.5 text-blue-500 rtl:ml-2 rtl:mr-0" />
                        {highlightSearchTerm(item.name, searchTerm)}
                      </p>
                      {(item.code ||
                        item.regionId ||
                        areas?.find((area) => area.ID === item.id)?.depart) && (
                        <div
                          className={cn(
                            'truncate text-xs transition-colors',
                            isFocused
                              ? 'text-slate-600 dark:text-slate-300'
                              : 'text-slate-500 dark:text-slate-400',
                          )}
                        >
                          {item.code && item.regionId ? (
                            <>
                              <Hash className="mr-1 inline h-3 w-3 text-slate-400 rtl:ml-1 rtl:mr-0" />
                              {item.code} -
                              <MapPin className="mx-1 inline h-3 w-3 text-slate-400" />
                              {areas?.find((area) => area.ID === item.regionId)
                                ?.Name || `Region ${item.regionId}`}
                              {areas?.find((area) => area.ID === item.regionId)
                                ?.depart && (
                                <Badge
                                  variant="soft"
                                  className="mr-2 text-xs rtl:ml-2 rtl:mr-0"
                                >
                                  {
                                    areas.find(
                                      (area) => area.ID === item.regionId,
                                    )?.depart
                                  }
                                </Badge>
                              )}
                            </>
                          ) : item.code && !item.regionId ? (
                            <>
                              <Hash className="mr-1 inline h-3 w-3 text-slate-400 rtl:ml-1 rtl:mr-0" />
                              {item.code}
                              {areas?.find((area) => area.ID === item.id)
                                ?.depart && (
                                <Badge
                                  variant="soft"
                                  className="mr-2 text-xs rtl:ml-2 rtl:mr-0"
                                >
                                  {
                                    areas.find((area) => area.ID === item.id)
                                      ?.depart
                                  }
                                </Badge>
                              )}
                            </>
                          ) : item.regionId ? (
                            <>
                              <MapPin className="mr-1 inline h-3 w-3 text-slate-400 rtl:ml-1 rtl:mr-0" />
                              {areas?.find((area) => area.ID === item.regionId)
                                ?.Name || `Region ${item.regionId}`}
                              {areas?.find((area) => area.ID === item.regionId)
                                ?.depart && (
                                <Badge
                                  variant="soft"
                                  className="mr-2 text-xs rtl:ml-2 rtl:mr-0"
                                >
                                  {
                                    areas.find(
                                      (area) => area.ID === item.regionId,
                                    )?.depart
                                  }
                                </Badge>
                              )}
                            </>
                          ) : areas?.find((area) => area.ID === item.id)
                              ?.depart ? (
                            <Badge variant="soft" className="text-xs">
                              {
                                areas.find((area) => area.ID === item.id)
                                  ?.depart
                              }
                            </Badge>
                          ) : null}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'transition-all duration-200',
                        isFocused
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100',
                      )}
                    >
                      {actionIcon === 'add' ? (
                        <ChevronLeft
                          className={cn(
                            'h-4 w-4 transition-colors',
                            isFocused
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-slate-400',
                          )}
                        />
                      ) : (
                        <X
                          className={cn(
                            'h-4 w-4 transition-colors',
                            isFocused
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-slate-400',
                          )}
                        />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TwoColumnSelectList({
  availableItems,
  selectedItems,
  onSelectionChange,
  title,
  searchPlaceholder,
  emptyMessage = 'لا توجد عناصر متاحة',
  className,
  selectedEmployee,
  avatarServerPath,
  areas,
}: TwoColumnSelectListProps) {
  const [availableSearchTerm, setAvailableSearchTerm] = useState('');
  const [selectedSearchTerm, setSelectedSearchTerm] = useState('');
  const [availableFocusedIndex, setAvailableFocusedIndex] = useState(-1);
  const [selectedFocusedIndex, setSelectedFocusedIndex] = useState(-1);
  const [availableSearchFocused, setAvailableSearchFocused] = useState(false);
  const [selectedSearchFocused, setSelectedSearchFocused] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState<
    'all' | 'جملة قطاعي' | 'unknown' | string
  >('all');

  const availableInputRef = useRef<HTMLInputElement>(null);
  const selectedInputRef = useRef<HTMLInputElement>(null);

  // Extract unique departments from areas
  const uniqueDepartments = useMemo(() => {
    if (!areas) return [];

    const departmentSet = new Set<string>();
    areas.forEach((area) => {
      if (area.depart && area.depart.trim()) {
        departmentSet.add(area.depart);
      } else {
        departmentSet.add('unknown');
      }
    });

    return Array.from(departmentSet).sort();
  }, [areas]);

  // Filter available items based on search and exclude already selected
  const filteredAvailableItems = useMemo(() => {
    return availableItems.filter((item) => {
      // First check if item is not already selected
      if (selectedItems.includes(item.id)) return false;

      // Check search term match
      const matchesSearch =
        item.name.toLowerCase().includes(availableSearchTerm.toLowerCase()) ||
        item.code?.toLowerCase().includes(availableSearchTerm.toLowerCase()) ||
        item.area?.toLowerCase().includes(availableSearchTerm.toLowerCase());

      if (!matchesSearch) return false;

      // Check department filter
      if (departmentFilter !== 'all' && areas) {
        // For items with regionId (like clients)
        if (item.regionId) {
          const itemArea = areas.find((area) => area.ID === item.regionId);
          if (!itemArea) return false;

          switch (departmentFilter) {
            case 'جملة قطاعي':
              return itemArea.depart === 'جملة قطاعي';
            case 'unknown':
              return !itemArea.depart || itemArea.depart.trim() === '';
            default:
              return itemArea.depart === departmentFilter;
          }
        }
        // For area items themselves (direct area filtering)
        else {
          const itemArea = areas.find((area) => area.ID === item.id);
          if (!itemArea) return false;

          switch (departmentFilter) {
            case 'جملة قطاعي':
              return itemArea.depart === 'جملة قطاعي';
            case 'unknown':
              return !itemArea.depart || itemArea.depart.trim() === '';
            default:
              return itemArea.depart === departmentFilter;
          }
        }
      }

      return true;
    });
  }, [
    availableItems,
    selectedItems,
    availableSearchTerm,
    departmentFilter,
    areas,
  ]);

  // Filter selected items based on search
  const filteredSelectedItems = useMemo(() => {
    const selectedItemsData = availableItems.filter((item) =>
      selectedItems.includes(item.id),
    );
    return selectedItemsData.filter((item) => {
      // Check search term match
      const matchesSearch =
        item.name.toLowerCase().includes(selectedSearchTerm.toLowerCase()) ||
        item.code?.toLowerCase().includes(selectedSearchTerm.toLowerCase()) ||
        item.area?.toLowerCase().includes(selectedSearchTerm.toLowerCase());

      if (!matchesSearch) return false;

      // Check department filter
      if (departmentFilter !== 'all' && areas) {
        // For items with regionId (like clients)
        if (item.regionId) {
          const itemArea = areas.find((area) => area.ID === item.regionId);
          if (!itemArea) return false;

          switch (departmentFilter) {
            case 'جملة قطاعي':
              return itemArea.depart === 'جملة قطاعي';
            case 'unknown':
              return !itemArea.depart || itemArea.depart.trim() === '';
            default:
              return itemArea.depart === departmentFilter;
          }
        }
        // For area items themselves (direct area filtering)
        else {
          const itemArea = areas.find((area) => area.ID === item.id);
          if (!itemArea) return false;

          switch (departmentFilter) {
            case 'جملة قطاعي':
              return itemArea.depart === 'جملة قطاعي';
            case 'unknown':
              return !itemArea.depart || itemArea.depart.trim() === '';
            default:
              return itemArea.depart === departmentFilter;
          }
        }
      }

      return true;
    });
  }, [
    availableItems,
    selectedItems,
    selectedSearchTerm,
    departmentFilter,
    areas,
  ]);

  // Get all selected items data (for display count)
  const selectedItemsData = useMemo(() => {
    return availableItems.filter((item) => selectedItems.includes(item.id));
  }, [availableItems, selectedItems]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent,
      items: SelectableItem[],
      focusedIndex: number,
      setFocusedIndex: (index: number) => void,
      onItemClick: (itemId: number) => void,
    ) => {
      if (items.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(Math.min(focusedIndex + 1, items.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(Math.max(focusedIndex - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < items.length) {
            onItemClick(items[focusedIndex].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setFocusedIndex(-1);
          break;
      }
    },
    [],
  );

  const handleAddItem = (itemId: number) => {
    if (!selectedItems.includes(itemId)) {
      onSelectionChange([...selectedItems, itemId]);
    }
  };

  const handleRemoveItem = (itemId: number) => {
    onSelectionChange(selectedItems.filter((id) => id !== itemId));
  };

  const handleAddAll = () => {
    const newSelectedItems = [
      ...selectedItems,
      ...filteredAvailableItems.map((item) => item.id),
    ];
    onSelectionChange([...new Set(newSelectedItems)]);
  };

  const handleRemoveAll = () => {
    onSelectionChange([]);
  };

  return (
    <div className={cn('space-y-4', className)}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-slate-900 dark:text-white">
            {title}
          </h4>
          <Badge variant="soft" className="text-sm">
            {selectedItems.length.toLocaleString()} محدد
          </Badge>
        </div>

        {selectedEmployee && (
          <div className="flex items-center space-x-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50 rtl:space-x-reverse">
            <Avatar className="h-8 w-8 border-2 border-white shadow-sm dark:border-slate-700">
              <AvatarImage
                src={
                  selectedEmployee.avatar && avatarServerPath
                    ? `${avatarServerPath}${selectedEmployee.avatar}`
                    : undefined
                }
                alt={selectedEmployee.empName}
              />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-xs font-semibold text-white">
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                <User className="mr-2 inline h-3.5 w-3.5 text-blue-500 rtl:ml-2 rtl:mr-0" />
                {selectedEmployee.empName}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                الموظف المحدد
              </p>
            </div>
          </div>
        )}

        {/* Department Filter */}
        {areas && areas.length > 0 && uniqueDepartments.length > 0 && (
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                تصفية المناطق حسب القسم:
              </span>
            </div>
            <Select
              value={departmentFilter}
              onValueChange={setDepartmentFilter}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="اختر القسم" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأقسام</SelectItem>
                {uniqueDepartments.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept === 'unknown' ? 'قسم غير محدد' : dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {departmentFilter !== 'all' && (
              <Badge variant="soft" className="text-xs">
                {filteredAvailableItems.length + filteredSelectedItems.length}{' '}
                عنصر
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Available Items Column */}
        <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <div className="border-b border-slate-200 p-4 dark:border-slate-700">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h5 className="font-medium text-slate-900 dark:text-white">
                  العناصر المتاحة (
                  {filteredAvailableItems.length.toLocaleString()})
                </h5>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddAll}
                  disabled={filteredAvailableItems.length === 0}
                  className="text-xs"
                >
                  إضافة الكل
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  ref={availableInputRef}
                  placeholder={searchPlaceholder}
                  value={availableSearchTerm}
                  onChange={(e) => {
                    setAvailableSearchTerm(e.target.value);
                    setAvailableFocusedIndex(0);
                  }}
                  onFocus={() => {
                    setAvailableSearchFocused(true);
                    setAvailableFocusedIndex(0);
                  }}
                  onBlur={() => {
                    setTimeout(() => setAvailableSearchFocused(false), 150);
                  }}
                  onKeyDown={(e) =>
                    handleKeyDown(
                      e,
                      filteredAvailableItems,
                      availableFocusedIndex,
                      setAvailableFocusedIndex,
                      handleAddItem,
                    )
                  }
                  className="pl-10"
                />
              </div>
            </div>
          </div>
          <VirtualizedList
            items={filteredAvailableItems}
            onItemClick={handleAddItem}
            actionIcon="add"
            emptyMessage={
              availableSearchTerm ? 'لا توجد نتائج للبحث' : emptyMessage
            }
            searchTerm={availableSearchTerm}
            focusedIndex={availableFocusedIndex}
            onFocusedIndexChange={setAvailableFocusedIndex}
            isSearchFocused={availableSearchFocused}
            areas={areas}
          />
        </div>

        {/* Selected Items Column */}
        <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <div className="border-b border-slate-200 p-4 dark:border-slate-700">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h5 className="font-medium text-slate-900 dark:text-white">
                  العناصر المحددة ({selectedItemsData.length.toLocaleString()})
                </h5>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveAll}
                  disabled={selectedItems.length === 0}
                  className="text-xs"
                >
                  إزالة الكل
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  ref={selectedInputRef}
                  placeholder="البحث في العناصر المحددة..."
                  value={selectedSearchTerm}
                  onChange={(e) => {
                    setSelectedSearchTerm(e.target.value);
                    setSelectedFocusedIndex(0);
                  }}
                  onFocus={() => {
                    setSelectedSearchFocused(true);
                    setSelectedFocusedIndex(0);
                  }}
                  onBlur={() => {
                    setTimeout(() => setSelectedSearchFocused(false), 150);
                  }}
                  onKeyDown={(e) =>
                    handleKeyDown(
                      e,
                      filteredSelectedItems,
                      selectedFocusedIndex,
                      setSelectedFocusedIndex,
                      handleRemoveItem,
                    )
                  }
                  className="pl-10"
                />
              </div>
            </div>
          </div>
          <VirtualizedList
            items={filteredSelectedItems}
            onItemClick={handleRemoveItem}
            actionIcon="remove"
            emptyMessage={
              selectedSearchTerm
                ? 'لا توجد نتائج للبحث'
                : 'لم يتم تحديد أي عناصر'
            }
            searchTerm={selectedSearchTerm}
            focusedIndex={selectedFocusedIndex}
            onFocusedIndexChange={setSelectedFocusedIndex}
            isSearchFocused={selectedSearchFocused}
            areas={areas}
          />
        </div>
      </div>
    </div>
  );
}
